import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { User } from '../../../shared/models/user';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { Guarantee } from '../../models/guarantee';
import { GuaranteeRequestService, REQUEST_FIELDS } from '../../services/guarantee-request.service';
import { DialogDeleteDraftComponent, RESPONSE_CONFIRM } from
	'../utils/dialog-delete-draft/dialog-delete-draft.component';
import { SnackbarDeleteDraftComponent } from
	'../../../claim/components/utils/snackbar-delete-draft/snackbar-delete-draft.component';
import { OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { LocationService } from '../../../shared/services/location.service';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';

@Component({
	selector: 'copo-guarantee-drafts',
	templateUrl: './guarantee-drafts.component.html',
	styleUrl: './guarantee-drafts.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		ScrollerDirective,
		PageHeaderComponent,
		LoadingSpinnerComponent,
		NgIf,
		AfterViewInitDirective,
		NgFor,
		MatIconModule,
		TranslateModule,
		MatButtonModule,

		MatTooltipModule,
		UpperCasePipe,
		DatePipe
	]
})
export class GuaranteeDraftsComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	user: User;
	isValidator = false;
	deleteDraftSubscription?: Subscription;
	draftList?: Guarantee[];
	draftSubscription?: Subscription;
	isLoading = false;

	constructor(
		private readonly router: Router,
		private readonly guaranteeRequestService: GuaranteeRequestService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,

		private readonly dialog: MatDialog,

		private readonly snackBar: MatSnackBar,
		private readonly locationService: LocationService
	) {
		this.user = this.userService.user;
		this.draftSubscription = this.guaranteeRequestService.getRequests(
			REQUEST_FIELDS,
			this.guaranteeRequestService.getByDraftStatusFilter(),
			1,
			100,
			[new OrderBy('lastModifiedDate', OrderByDirection.DESC)],
			false,
			false
		).subscribe(res => {
			this.draftList = res.results;
		});
	}

	ngOnDestroy() {
		this.draftSubscription?.unsubscribe();
		this.deleteDraftSubscription?.unsubscribe();
	}

	async eveEditDraft(draftId: string) {
		this.isLoading = true;
		await this.router.navigate(
			[`../guarantee-requests/edit/${ draftId }/project-characteristics`],
			{ replaceUrl: true }
		);
	}

	evtDeleteDraft(draft: Guarantee) {
		const path = '/guarantee-requests/drafts';
		const sub = this.dialog.open<DialogDeleteDraftComponent, number>(
			DialogDeleteDraftComponent,
			{ panelClass: 'copo-claim-dialog' }
		);
		sub.componentInstance.draftName = draft.name;
		this.deleteDraftSubscription = sub.afterClosed()
			.subscribe(async response => {
				if (response === RESPONSE_CONFIRM && draft.id !== undefined) {
					try {
						await this.guaranteeRequestService.deleteGuaranteeRequest(draft.id);
						this.snackBar.openFromComponent(
							SnackbarDeleteDraftComponent,
							{
								duration: 5000,
								panelClass: ['copo-draft-snackbar']
							}
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					} catch (err) {
						console.error('unable to delete draft', err);
						this.snackBar.open(
							this.translationService.getTR(
								'error.deleting.draft'
							),
							undefined,
							{
								duration: 5000,
							}
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					}
				}
			});
	}

	goBack() {
		this.locationService.go(-1);
	}
}
